import React, { useState, useCallback, useRef } from 'react';
import {
  FiRotateCw, FiRotateCcw, FiImage, FiUpload,
  FiSquare, FiCircle, FiTriangle, FiStar, FiHexagon, FiHeart,
  FiArrowRight, FiMinus, FiLock, FiUnlock,
} from 'react-icons/fi';
import * as fabric from 'fabric';
import useEditorStore from '../../../stores/useEditorStore';
import { createEmptyFrame, FRAME_SHAPES, createFrameClipPath, extractImageFromFrame } from '../../../utils/frameUtils';

// ── 커스텀 SVG 아이콘 (ShapeSettingsPanel과 동일) ──

const RoundedRectIcon = ({ size = 20, color = '#6b7280' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
    <rect x="3" y="3" width="18" height="18" rx="5" ry="5" />
  </svg>
);
const DiamondIcon = ({ size = 20, color = '#6b7280' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
    <polygon points="12,2 22,12 12,22 2,12" />
  </svg>
);
const CrossIcon = ({ size = 20, color = '#6b7280' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
    <polygon points="8,2 16,2 16,8 22,8 22,16 16,16 16,22 8,22 8,16 2,16 2,8 8,8" />
  </svg>
);
const PentagonIcon = ({ size = 20, color = '#6b7280' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
    <polygon points="12,2 22,9 19,21 5,21 2,9" />
  </svg>
);

const FRAME_SHAPE_ICONS = {
  rect: FiSquare,
  roundedRect: RoundedRectIcon,
  circle: FiCircle,
  triangle: FiTriangle,
  star: FiStar,
  diamond: DiamondIcon,
  pentagon: PentagonIcon,
  hexagon: FiHexagon,
  arrow: FiArrowRight,
  cross: CrossIcon,
  heart: FiHeart,
};

const FRAME_SHAPE_LABELS = {
  rect: '사각형',
  roundedRect: '둥근사각형',
  circle: '원',
  triangle: '삼각형',
  star: '별',
  diamond: '마름모',
  pentagon: '오각형',
  hexagon: '육각형',
  arrow: '화살표',
  cross: '십자가',
  heart: '하트',
};

/**
 * 원본 이미지에 마스크를 합성하여 data URL 반환
 */
const compositeWithMask = (imageUrl, maskUrl) => {
  return new Promise((resolve, reject) => {
    const origImg = new Image();
    const maskImg = new Image();
    let loaded = 0;

    const onBothLoaded = () => {
      const cvs = document.createElement('canvas');
      cvs.width = origImg.width;
      cvs.height = origImg.height;
      const ctx = cvs.getContext('2d');
      ctx.drawImage(origImg, 0, 0);

      const maskCvs = document.createElement('canvas');
      maskCvs.width = origImg.width;
      maskCvs.height = origImg.height;
      const maskCtx = maskCvs.getContext('2d');
      maskCtx.drawImage(maskImg, 0, 0, origImg.width, origImg.height);

      const imgData = ctx.getImageData(0, 0, cvs.width, cvs.height);
      const maskData = maskCtx.getImageData(0, 0, cvs.width, cvs.height);
      for (let i = 0; i < imgData.data.length; i += 4) {
        imgData.data[i + 3] = maskData.data[i];
      }
      ctx.putImageData(imgData, 0, 0);
      resolve(cvs.toDataURL('image/png'));
    };

    origImg.onload = () => { if (++loaded === 2) onBothLoaded(); };
    maskImg.onload = () => { if (++loaded === 2) onBothLoaded(); };
    origImg.onerror = reject;
    maskImg.onerror = reject;
    origImg.crossOrigin = 'anonymous';
    maskImg.crossOrigin = 'anonymous';
    origImg.src = imageUrl;
    maskImg.src = maskUrl;
  });
};

const ImageSettingsPanel = () => {
  const { canvas, canvasSize, selectedObjects, pushHistory, syncLayersFromCanvas } = useEditorStore();
  const [maskLoading, setMaskLoading] = useState(false);
  const fileInputRef = useRef(null);

  // 선택된 이미지 객체 (프레임이 아닌 일반 이미지)
  const selectedImageObject = selectedObjects.find(
    obj => obj.type === 'image' && !obj._isFrame && !obj._isChart
  );

  // 선택된 프레임 객체 (빈 프레임 또는 이미지가 들어간 프레임)
  const selectedFrameObject = selectedObjects.find(obj => obj._isFrame);

  // 어떤 패널을 보여줄지 결정
  const showFrameSettings = !!selectedFrameObject;
  const showImageSettings = !!selectedImageObject && !showFrameSettings;

  // ── 이미지 업로드 ──
  const handleImageUpload = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file || !canvas) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const img = await fabric.FabricImage.fromURL(ev.target.result, { crossOrigin: 'anonymous' });
        const maxW = canvasSize.width * 0.6;
        const maxH = canvasSize.height * 0.6;
        const scale = Math.min(maxW / img.width, maxH / img.height, 1);
        img.set({
          left: canvasSize.width / 2,
          top: canvasSize.height / 2,
          originX: 'center',
          originY: 'center',
          scaleX: scale,
          scaleY: scale,
          id: `image-${Date.now()}`,
          name: file.name || 'Image',
        });
        canvas.add(img);
        canvas.setActiveObject(img);
        canvas.renderAll();
        syncLayersFromCanvas();
        pushHistory();
      } catch (err) {
        console.error('이미지 로드 실패:', err);
      }
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  }, [canvas, canvasSize, pushHistory, syncLayersFromCanvas]);

  // ── 프레임 추가 ──
  const handleAddFrame = useCallback((shapeType) => {
    if (!canvas) return;
    const cx = (canvasSize.width - 300) / 2;
    const cy = (canvasSize.height - 300) / 2;
    const frame = createEmptyFrame(shapeType, cx, cy, 300);
    if (!frame) return;
    canvas.add(frame);
    canvas.setActiveObject(frame);
    canvas.renderAll();
    syncLayersFromCanvas();
    pushHistory();
  }, [canvas, canvasSize, pushHistory, syncLayersFromCanvas]);

  // ── 이미지 설정 핸들러 ──
  const handleImagePropertyChange = (property, value) => {
    if (!selectedImageObject || !canvas) return;
    selectedImageObject.set(property, value);
    canvas.renderAll();
    pushHistory();
  };

  const handleRemoveMask = useCallback(async () => {
    if (!selectedImageObject || !canvas || !selectedImageObject._originalImageUrl) return;
    setMaskLoading(true);
    try {
      const origUrl = selectedImageObject._originalImageUrl;
      const newImg = await fabric.FabricImage.fromURL(origUrl, { crossOrigin: 'anonymous' });
      const scaleX = (selectedImageObject.width * selectedImageObject.scaleX) / newImg.width;
      const scaleY = (selectedImageObject.height * selectedImageObject.scaleY) / newImg.height;
      selectedImageObject.setElement(newImg.getElement());
      selectedImageObject.set({ scaleX, scaleY });
      selectedImageObject._maskApplied = false;
      canvas.renderAll();
      pushHistory();
    } catch (e) {
      console.error('마스크 제거 오류:', e);
    }
    setMaskLoading(false);
  }, [selectedImageObject, canvas, pushHistory]);

  const handleApplyMask = useCallback(async () => {
    if (!selectedImageObject || !canvas || !selectedImageObject._originalImageUrl || !selectedImageObject._maskUrl) return;
    setMaskLoading(true);
    try {
      const composited = await compositeWithMask(
        selectedImageObject._originalImageUrl,
        selectedImageObject._maskUrl,
      );
      const newImg = await fabric.FabricImage.fromURL(composited, { crossOrigin: 'anonymous' });
      const scaleX = (selectedImageObject.width * selectedImageObject.scaleX) / newImg.width;
      const scaleY = (selectedImageObject.height * selectedImageObject.scaleY) / newImg.height;
      selectedImageObject.setElement(newImg.getElement());
      selectedImageObject.set({ scaleX, scaleY });
      selectedImageObject._maskApplied = true;
      canvas.renderAll();
      pushHistory();
    } catch (e) {
      console.error('마스크 적용 오류:', e);
    }
    setMaskLoading(false);
  }, [selectedImageObject, canvas, pushHistory]);

  const handleRotate = (obj, angle) => {
    if (!obj || !canvas) return;
    obj.rotate((obj.angle || 0) + angle);
    canvas.renderAll();
    pushHistory();
  };

  const handleFlip = (obj, direction) => {
    if (!obj || !canvas) return;
    if (direction === 'horizontal') {
      obj.set('flipX', !obj.flipX);
    } else {
      obj.set('flipY', !obj.flipY);
    }
    canvas.renderAll();
    pushHistory();
  };

  // ── 프레임 설정 핸들러 ──
  const handleFrameShapeChange = useCallback((newShapeType) => {
    if (!selectedFrameObject || !canvas) return;

    if (selectedFrameObject._isEmptyFrame) {
      // 빈 프레임: 도형 교체 (새로 생성)
      const center = selectedFrameObject.getCenterPoint();
      const w = (selectedFrameObject.width || 150) * (selectedFrameObject.scaleX || 1);
      const h = (selectedFrameObject.height || 150) * (selectedFrameObject.scaleY || 1);
      const locked = selectedFrameObject._frameLocked;
      const idx = canvas.getObjects().indexOf(selectedFrameObject);

      canvas.remove(selectedFrameObject);

      const newFrame = createEmptyFrame(newShapeType, 0, 0, Math.min(w, h));
      if (!newFrame) return;
      newFrame.set({
        left: center.x,
        top: center.y,
        originX: 'center',
        originY: 'center',
        scaleX: w / (newFrame.width || 1),
        scaleY: h / (newFrame.height || 1),
      });
      newFrame._frameLocked = locked;
      if (idx >= 0 && idx < canvas.getObjects().length) {
        canvas.insertAt(idx, newFrame);
      } else {
        canvas.add(newFrame);
      }
      canvas.setActiveObject(newFrame);
    } else {
      // 이미지가 있는 프레임: clipPath 교체
      const frameW = selectedFrameObject._frameWidth || (selectedFrameObject.width * selectedFrameObject.scaleX);
      const frameH = selectedFrameObject._frameHeight || (selectedFrameObject.height * selectedFrameObject.scaleY);
      const clipPath = createFrameClipPath(
        newShapeType,
        frameW / (selectedFrameObject.scaleX || 1),
        frameH / (selectedFrameObject.scaleY || 1),
      );
      selectedFrameObject.clipPath = clipPath;
      selectedFrameObject._frameShapeType = newShapeType;
    }
    canvas.renderAll();
    syncLayersFromCanvas();
    pushHistory();
  }, [selectedFrameObject, canvas, pushHistory, syncLayersFromCanvas]);

  const handleFrameLockToggle = useCallback(() => {
    if (!selectedFrameObject || !canvas) return;
    selectedFrameObject._frameLocked = !selectedFrameObject._frameLocked;
    canvas.renderAll();
    pushHistory();
  }, [selectedFrameObject, canvas, pushHistory]);

  const handleExtractImage = useCallback(() => {
    if (!selectedFrameObject || !canvas || selectedFrameObject._isEmptyFrame) return;
    extractImageFromFrame(selectedFrameObject, canvas);
    syncLayersFromCanvas();
    pushHistory();
  }, [selectedFrameObject, canvas, pushHistory, syncLayersFromCanvas]);

  // 현재 활성 객체 (이미지 또는 프레임)
  const activeObj = selectedFrameObject || selectedImageObject;

  return (
    <div style={panelStyle}>
      {/* ━━━━ 프레임 설정 (프레임 선택 시) ━━━━ */}
      {showFrameSettings && (
        <>
          <h3 style={titleStyle}>프레임 설정</h3>

          {/* 프레임 모양 변경 */}
          <div style={sectionStyle}>
            <label style={labelStyle}>프레임 모양</label>
            <div style={frameGridStyle}>
              {FRAME_SHAPES.map((shapeId) => {
                const Icon = FRAME_SHAPE_ICONS[shapeId];
                const isActive = selectedFrameObject._frameShapeType === shapeId;
                return (
                  <button
                    key={shapeId}
                    onClick={() => handleFrameShapeChange(shapeId)}
                    style={{
                      ...frameShapeButtonStyle,
                      backgroundColor: isActive ? '#eff6ff' : '#ffffff',
                      borderColor: isActive ? '#3b82f6' : '#e5e7eb',
                      color: isActive ? '#3b82f6' : '#6b7280',
                    }}
                    title={FRAME_SHAPE_LABELS[shapeId]}
                  >
                    {typeof Icon === 'function' && Icon.prototype?.render
                      ? <Icon size={20} color={isActive ? '#3b82f6' : '#6b7280'} />
                      : <Icon size={20} />}
                  </button>
                );
              })}
            </div>
          </div>

          {/* 잠금 */}
          <div style={sectionStyle}>
            <button
              onClick={handleFrameLockToggle}
              style={{
                ...actionButtonStyle,
                width: '100%',
                flexDirection: 'row',
                justifyContent: 'center',
                backgroundColor: selectedFrameObject._frameLocked ? '#fef2f2' : '#ffffff',
                borderColor: selectedFrameObject._frameLocked ? '#ef4444' : '#e5e7eb',
                color: selectedFrameObject._frameLocked ? '#ef4444' : '#374151',
              }}
            >
              {selectedFrameObject._frameLocked ? <FiLock size={16} /> : <FiUnlock size={16} />}
              <span style={{ marginLeft: '8px' }}>
                {selectedFrameObject._frameLocked ? '프레임 잠금됨' : '프레임 잠금 해제'}
              </span>
            </button>
          </div>

          {/* 이미지 빼내기 (이미지가 있을 때만) */}
          {!selectedFrameObject._isEmptyFrame && (
            <div style={sectionStyle}>
              <button
                onClick={handleExtractImage}
                style={{ ...actionButtonStyle, width: '100%', flexDirection: 'row', justifyContent: 'center' }}
              >
                <FiImage size={16} />
                <span style={{ marginLeft: '8px' }}>사진 빼내기</span>
              </button>
            </div>
          )}

          {/* 투명도 */}
          <div style={sectionStyle}>
            <label style={labelStyle}>
              투명도: {Math.round((selectedFrameObject.opacity ?? 1) * 100)}%
            </label>
            <input
              type="range" min="0" max="100"
              value={(selectedFrameObject.opacity ?? 1) * 100}
              onChange={(e) => {
                selectedFrameObject.set('opacity', parseInt(e.target.value) / 100);
                canvas.renderAll();
                pushHistory();
              }}
              style={{ width: '100%' }}
            />
          </div>

          {/* 회전 */}
          <div style={sectionStyle}>
            <label style={labelStyle}>회전: {Math.round(selectedFrameObject.angle || 0)}°</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={() => handleRotate(selectedFrameObject, -90)} style={actionButtonStyle}>
                <FiRotateCcw size={18} /><span>-90°</span>
              </button>
              <button onClick={() => handleRotate(selectedFrameObject, 90)} style={actionButtonStyle}>
                <FiRotateCw size={18} /><span>+90°</span>
              </button>
            </div>
          </div>

          {/* 프레임 정보 */}
          <div style={sectionStyle}>
            <label style={labelStyle}>프레임 정보</label>
            <div style={infoBoxStyle}>
              <div style={{ marginBottom: '4px' }}>
                <strong>모양:</strong> {FRAME_SHAPE_LABELS[selectedFrameObject._frameShapeType] || '사각형'}
              </div>
              <div style={{ marginBottom: '4px' }}>
                <strong>상태:</strong> {selectedFrameObject._isEmptyFrame ? '비어있음' : '이미지 있음'}
              </div>
              <div>
                <strong>위치:</strong> ({Math.round(selectedFrameObject.left || 0)}, {Math.round(selectedFrameObject.top || 0)})
              </div>
            </div>
          </div>
        </>
      )}

      {/* ━━━━ 이미지 설정 (일반 이미지 선택 시) ━━━━ */}
      {showImageSettings && (
        <>
          <h3 style={titleStyle}>이미지 설정</h3>

          {/* 투명도 */}
          <div style={sectionStyle}>
            <label style={labelStyle}>
              투명도: {Math.round((selectedImageObject.opacity ?? 1) * 100)}%
            </label>
            <input
              type="range" min="0" max="100"
              value={(selectedImageObject.opacity ?? 1) * 100}
              onChange={(e) => handleImagePropertyChange('opacity', parseInt(e.target.value) / 100)}
              style={{ width: '100%' }}
            />
          </div>

          {/* 회전 */}
          <div style={sectionStyle}>
            <label style={labelStyle}>회전: {Math.round(selectedImageObject.angle || 0)}°</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={() => handleRotate(selectedImageObject, -90)} style={actionButtonStyle}>
                <FiRotateCcw size={18} /><span>-90°</span>
              </button>
              <button onClick={() => handleRotate(selectedImageObject, 90)} style={actionButtonStyle}>
                <FiRotateCw size={18} /><span>+90°</span>
              </button>
            </div>
            <input
              type="range" min="0" max="360"
              value={selectedImageObject.angle || 0}
              onChange={(e) => {
                selectedImageObject.rotate(parseInt(e.target.value));
                canvas.renderAll();
                pushHistory();
              }}
              style={{ width: '100%', marginTop: '8px' }}
            />
          </div>

          {/* 뒤집기 */}
          <div style={sectionStyle}>
            <label style={labelStyle}>뒤집기</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => handleFlip(selectedImageObject, 'horizontal')}
                style={{
                  ...actionButtonStyle,
                  backgroundColor: selectedImageObject.flipX ? '#eff6ff' : '#ffffff',
                  borderColor: selectedImageObject.flipX ? '#3b82f6' : '#e5e7eb',
                  color: selectedImageObject.flipX ? '#3b82f6' : '#374151',
                }}
              >
                <span>↔ 좌우</span>
              </button>
              <button
                onClick={() => handleFlip(selectedImageObject, 'vertical')}
                style={{
                  ...actionButtonStyle,
                  backgroundColor: selectedImageObject.flipY ? '#eff6ff' : '#ffffff',
                  borderColor: selectedImageObject.flipY ? '#3b82f6' : '#e5e7eb',
                  color: selectedImageObject.flipY ? '#3b82f6' : '#374151',
                }}
              >
                <span>↕ 상하</span>
              </button>
            </div>
          </div>

          {/* 크기 */}
          <div style={sectionStyle}>
            <label style={labelStyle}>크기: {Math.round((selectedImageObject.scaleX || 1) * 100)}%</label>
            <input
              type="range" min="10" max="200"
              value={(selectedImageObject.scaleX || 1) * 100}
              onChange={(e) => {
                const scale = parseInt(e.target.value) / 100;
                selectedImageObject.set({ scaleX: scale, scaleY: scale });
                canvas.renderAll();
                pushHistory();
              }}
              style={{ width: '100%' }}
            />
          </div>

          {/* 마스크 */}
          {selectedImageObject._hasMask && (
            <div style={sectionStyle}>
              <label style={labelStyle}>마스크</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                {selectedImageObject._maskApplied !== false ? (
                  <button onClick={handleRemoveMask} disabled={maskLoading}
                    style={{ ...actionButtonStyle, flex: 1, opacity: maskLoading ? 0.5 : 1 }}>
                    {maskLoading ? '처리중...' : '마스크 제거'}
                  </button>
                ) : (
                  <button onClick={handleApplyMask} disabled={maskLoading}
                    style={{ ...actionButtonStyle, flex: 1, backgroundColor: '#eff6ff', borderColor: '#3b82f6', color: '#3b82f6', opacity: maskLoading ? 0.5 : 1 }}>
                    {maskLoading ? '처리중...' : '마스크 적용'}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* 이미지 정보 */}
          <div style={sectionStyle}>
            <label style={labelStyle}>이미지 정보</label>
            <div style={infoBoxStyle}>
              <div style={{ marginBottom: '4px' }}>
                <strong>원본 크기:</strong> {Math.round(selectedImageObject.width || 0)} x {Math.round(selectedImageObject.height || 0)}px
              </div>
              <div style={{ marginBottom: '4px' }}>
                <strong>현재 크기:</strong> {Math.round((selectedImageObject.width || 0) * (selectedImageObject.scaleX || 1))} x {Math.round((selectedImageObject.height || 0) * (selectedImageObject.scaleY || 1))}px
              </div>
              <div>
                <strong>위치:</strong> ({Math.round(selectedImageObject.left || 0)}, {Math.round(selectedImageObject.top || 0)})
              </div>
            </div>
          </div>
        </>
      )}

      {/* ━━━━ 기본 화면 (아무것도 선택 안 됨) ━━━━ */}
      {!showFrameSettings && !showImageSettings && (
        <>
          <h3 style={titleStyle}>이미지</h3>

          {/* 이미지 업로드 */}
          <div style={sectionStyle}>
            <label style={labelStyle}>이미지 업로드</label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              style={{ display: 'none' }}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              style={uploadButtonStyle}
            >
              <FiUpload size={24} color="#6b7280" />
              <span style={{ fontSize: '13px', color: '#6b7280', marginTop: '8px' }}>
                클릭하여 이미지 업로드
              </span>
            </button>
          </div>

          {/* 프레임 추가 */}
          <div style={sectionStyle}>
            <label style={labelStyle}>프레임</label>
            <p style={{ fontSize: '11px', color: '#9ca3af', margin: '0 0 8px 0' }}>
              프레임을 추가하고 이미지를 드래그하여 넣으세요
            </p>
            <div style={frameGridStyle}>
              {FRAME_SHAPES.map((shapeId) => {
                const Icon = FRAME_SHAPE_ICONS[shapeId];
                return (
                  <button
                    key={shapeId}
                    onClick={() => handleAddFrame(shapeId)}
                    style={frameShapeButtonStyle}
                    title={FRAME_SHAPE_LABELS[shapeId]}
                  >
                    {typeof Icon === 'function' && Icon.prototype?.render
                      ? <Icon size={20} color="#6b7280" />
                      : <Icon size={20} />}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ── 스타일 ──

const panelStyle = {
  padding: '16px',
  height: '100%',
  overflowY: 'auto',
};

const titleStyle = {
  fontSize: '16px',
  fontWeight: '600',
  color: '#1f2937',
  marginBottom: '20px',
  paddingBottom: '12px',
  borderBottom: '1px solid #e5e7eb',
};

const sectionStyle = {
  marginBottom: '20px',
};

const labelStyle = {
  display: 'block',
  fontSize: '13px',
  fontWeight: '500',
  color: '#374151',
  marginBottom: '8px',
};

const actionButtonStyle = {
  flex: 1,
  padding: '10px 8px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '4px',
  border: '1px solid #e5e7eb',
  borderRadius: '8px',
  backgroundColor: '#ffffff',
  cursor: 'pointer',
  fontSize: '12px',
  color: '#374151',
};

const uploadButtonStyle = {
  width: '100%',
  padding: '24px 16px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  border: '2px dashed #d1d5db',
  borderRadius: '12px',
  backgroundColor: '#f9fafb',
  cursor: 'pointer',
};

const frameGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  gap: '6px',
};

const frameShapeButtonStyle = {
  width: '100%',
  aspectRatio: '1',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid #e5e7eb',
  borderRadius: '8px',
  backgroundColor: '#ffffff',
  cursor: 'pointer',
  color: '#6b7280',
};

const infoBoxStyle = {
  fontSize: '12px',
  color: '#6b7280',
  backgroundColor: '#f3f4f6',
  padding: '12px',
  borderRadius: '8px',
};

export default ImageSettingsPanel;
